import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faSearch,
  faTimes,
  faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";

const PreSelectModal = React.memo(
  ({
    showPreSelectModal,
    setShowPreSelectModal,
    activeCategory,
    handleCategoryChange,
    searchTerm,
    setSearchTerm,
    finalFilteredCoverages,
    handlePreSelectCoverage,
    handleSkipPreSelect,
    searchInputRef,
    categories,
  }) => (
    <AnimatePresence>
      {showPreSelectModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-y-0 left-0 tracker-aware-modal-layer bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
          onClick={() => setShowPreSelectModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl text-gray-900 flex items-center gap-2">
                <FontAwesomeIcon
                  icon={faShieldAlt}
                  className="text-indigo-600"
                />
                Quick Coverage Pre-Selection
              </h2>
              <button
                onClick={() => setShowPreSelectModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FontAwesomeIcon icon={faTimes} className="text-xl" />
              </button>
            </div>
            <p className="text-gray-600 mb-6 text-center">
              Browse coverages by category for faster selection. It's fast and
              cached for instant response! Or skip to choose manually.
            </p>

            {/* Category Tabs */}
            <div className="flex flex-wrap justify-center gap-2 mb-6 bg-gray-100 p-2 rounded-lg">
              {categories.map((category) => (
                <motion.button
                  key={category.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-medium transition-all duration-200 ${
                    activeCategory === category.id
                      ? "bg-indigo-600 text-white shadow-md"
                      : "bg-white text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  <FontAwesomeIcon icon={category.icon} className="text-sm" />
                  {category.label}
                </motion.button>
              ))}
            </div>

            {/* Search Input */}
            <div className="mb-6">
              <div className="relative">
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  placeholder="Search coverages by name or ID..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
            </div>

            {/* Coverage Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
              {finalFilteredCoverages.length > 0 ? (
                finalFilteredCoverages.map((coverage) => (
                  <motion.div
                    key={coverage.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    whileHover={{
                      scale: 1.05,
                      y: -5,
                      boxShadow:
                        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-200 cursor-pointer hover:border-indigo-400 transition-all duration-200 shadow-sm group"
                    onClick={() => handlePreSelectCoverage(coverage)}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <FontAwesomeIcon
                            icon={faShieldAlt}
                            className="text-indigo-600 text-lg"
                          />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900 text-sm">
                            {coverage.text}
                          </h4>
                          <p className="text-xs text-gray-500">
                            ID: {coverage.id}
                          </p>
                        </div>
                      </div>
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{
                          duration: 1,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="text-indigo-500 text-xl opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <FontAwesomeIcon icon={faPlusCircle} />
                      </motion.div>
                    </div>
                    <p className="text-xs text-gray-600 leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity">
                      Select this coverage to pre-populate your form. Instant
                      and secure.
                    </p>
                  </motion.div>
                ))
              ) : (
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="col-span-full text-center text-gray-500 py-8"
                >
                  {searchTerm
                    ? `No coverages found for "${searchTerm}". Try another search or category!`
                    : "No coverages found in this category. Try another one!"}
                </motion.p>
              )}
            </div>

            <div className="flex justify-center">
              <motion.button
                whileHover={{ scale: 1.02 }}
                className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-all duration-200"
                onClick={handleSkipPreSelect}
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />I don't want
                to pre-select
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
);

export default PreSelectModal;
